import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll, useReducedMotion, useAnimationFrame } from "framer-motion";
import { radrootConfig } from "@/config/radrootConfig";
import { scrollToAnchor } from "./primitives";

const lineAnim = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.13, ease: [0.16, 1, 0.3, 1] },
  }),
};

const BURST_RAYS = Array.from({ length: 20 }, (_, i) => (i * 360) / 20);

const STAR4 = "M0 -11 L2.8 -2.8 L11 0 L2.8 2.8 L0 11 L-2.8 2.8 L-11 0 L-2.8 -2.8 Z";
const STAR5 = "M0 -12 L2.94 -4.05 L11.41 -3.71 L4.76 1.55 L7.05 9.71 L0 5 L-7.05 9.71 L-4.76 1.55 L-11.41 -3.71 L-2.94 -4.05 Z";

const ELLIPSE_MOVERS = [
  { type: "dot", angle0: 0, period: 46 },
  { type: "star", angle0: 130, period: 46 },
  { type: "dot-hollow", angle0: 235, period: 46 },
  { type: "dot", angle0: 300, period: 46 },
  { type: "star-outline", angle0: 60, period: 46 },
];

function ellipsePos(angleDeg, rx, ry, rotDeg, cx, cy) {
  const a = (angleDeg * Math.PI) / 180;
  const rot = (rotDeg * Math.PI) / 180;
  const px = rx * Math.cos(a);
  const py = ry * Math.sin(a);
  return {
    x: cx + px * Math.cos(rot) - py * Math.sin(rot),
    y: cy + px * Math.sin(rot) + py * Math.cos(rot),
  };
}

function EllipseMover({ cfg, reduce }) {
  const ref = useRef(null);
  useAnimationFrame((t) => {
    if (!ref.current) return;
    const angle = reduce ? cfg.angle0 : cfg.angle0 + (t / 1000) * (360 / cfg.period);
    const { x, y } = ellipsePos(angle, 172, 48, -18, 200, 375);
    ref.current.setAttribute("transform", `translate(${x} ${y})`);
  });
  const start = ellipsePos(cfg.angle0, 172, 48, -18, 200, 375);
  return (
    <g ref={ref} transform={`translate(${start.x} ${start.y})`}>
      {cfg.type === "dot" && <circle r="3.2" fill="var(--grn-hi)" stroke="none" />}
      {cfg.type === "dot-hollow" && <circle r="2.8" stroke="currentColor" strokeWidth="1.2" fill="none" />}
      {cfg.type === "star" && <path d={STAR4} fill="var(--grn-hi)" stroke="none" />}
      {cfg.type === "star-outline" && <path d={STAR4} stroke="currentColor" strokeWidth="1.2" fill="none" />}
    </g>
  );
}

function RingDot({ reduce }) {
  const ref = useRef(null);
  useAnimationFrame((t) => {
    if (!ref.current) return;
    const angle = reduce ? 40 : 40 + (t / 1000) * (360 / 30);
    const rad = (angle * Math.PI) / 180;
    ref.current.setAttribute("transform", `translate(${200 + 118 * Math.cos(rad)} ${375 + 118 * Math.sin(rad)})`);
  });
  return (
    <g ref={ref}>
      <circle r="3" fill="var(--grn-hi)" stroke="none" />
    </g>
  );
}

function InnerDot({ reduce }) {
  const ref = useRef(null);
  useAnimationFrame((t) => {
    if (!ref.current) return;
    const angle = reduce ? 150 : 150 - (t / 1000) * (360 / 24);
    const rad = (angle * Math.PI) / 180;
    ref.current.setAttribute("transform", `translate(${200 + 95 * Math.cos(rad)} ${375 + 95 * Math.sin(rad)})`);
  });
  return (
    <g ref={ref}>
      <circle r="2.6" fill="var(--grn-hi)" stroke="none" />
    </g>
  );
}

export default function Hero() {
  const reduce = useReducedMotion();
  const visualRef = useRef(null);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 55, damping: 16 });
  const rY = useSpring(useTransform(mx, [-0.5, 0.5], [-7, 7]), { stiffness: 55, damping: 16 });

  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const ghostY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const markY = useTransform(scrollYProgress, [0, 1], [0, 90]);

  const onMove = (e) => {
    if (reduce || !visualRef.current) return;
    const r = visualRef.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  const headline = [
    <>
      Radroot
      <span className="grn">for Flare</span>
    </>,
  ];

  return (
    <section className="hero" id="top" ref={heroRef} data-testid="hero-section">
      <div className="hero-bg" aria-hidden="true" />
      <div className="hero-glow" aria-hidden="true" />
      <motion.span className="hero-ghost" style={{ y: reduce ? undefined : ghostY }} aria-hidden="true">
        √
      </motion.span>

      <div className="wrap hero-grid">
        <div>
          <motion.div
            initial={reduce ? undefined : { opacity: 0, y: 14 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="hero-badge" data-testid="hero-status-badge">
              <i className="dot" aria-hidden="true" />
              {radrootConfig.status.badge}
            </span>
          </motion.div>

          <h1 className="hero-title" data-testid="hero-headline">
            {headline.map((l, i) => (
              <span className="line" key={i}>
                <motion.span
                  style={{ display: "block" }}
                  variants={lineAnim}
                  initial={reduce ? undefined : "hidden"}
                  animate={reduce ? undefined : "show"}
                  custom={i}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            className="hero-desc"
            data-testid="hero-description"
            initial={reduce ? undefined : { opacity: 0, y: 18 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
          >
            Radroot operates an FTSO data provider and validator infrastructure on the Flare network.
          </motion.p>

          <motion.div
            className="hero-ctas"
            initial={reduce ? undefined : { opacity: 0, y: 18 }}
            animate={reduce ? undefined : { opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <a className="btn btn-solid" href="#infrastructure" data-testid="hero-cta-infra" onClick={(e) => scrollToAnchor(e, "#infrastructure")}>
              View infrastructure
            </a>
            <a className="btn btn-ghost" href="#about" data-testid="hero-cta-about" onClick={(e) => scrollToAnchor(e, "#about")}>
              About Radroot
            </a>
          </motion.div>
        </div>

        <motion.div
          className="hero-visual"
          ref={visualRef}
          onMouseMove={onMove}
          onMouseLeave={() => {
            mx.set(0);
            my.set(0);
          }}
          style={{ perspective: 900 }}
          initial={reduce ? undefined : { opacity: 0, scale: 0.92 }}
          animate={reduce ? undefined : { opacity: 1, scale: 1 }}
          transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
          data-testid="hero-visual"
        >
          <div className="glow" aria-hidden="true" />
          <motion.div style={{ y: reduce ? undefined : markY, rotateX: rX, rotateY: rY, transformStyle: "preserve-3d" }} className="hero-mark-wrap">
            <svg viewBox="0 0 400 620" fill="none" className="hero-svg" data-testid="hero-solar" role="img" aria-label="Radroot provider system diagram">
              <line x1="200" y1="60" x2="200" y2="600" stroke="currentColor" strokeWidth="1.4" strokeDasharray="1 8" opacity="0.5" className="axis-line" />
              <path d="M 162 58 A 38 38 0 0 1 238 58" stroke="currentColor" strokeWidth="1.4" />
              <circle cx="200" cy="64" r="6.5" fill="var(--grn-hi)" stroke="none" className="pulse-dot" />
              <g className="burst-g" stroke="currentColor" strokeWidth="1.3" opacity="0.9">
                {BURST_RAYS.map((deg) => (
                  <line key={deg} x1="200" y1="131" x2="200" y2="112" transform={`rotate(${deg} 200 155)`} />
                ))}
                <circle cx="200" cy="155" r="7" fill="var(--grn-hi)" stroke="none" />
              </g>
              <circle cx="200" cy="240" r="13" stroke="currentColor" strokeWidth="1.2" opacity="0.8" />
              <circle cx="243" cy="260" r="11" stroke="currentColor" strokeWidth="1.1" opacity="0.65" />
              <circle cx="260" cy="268" r="6.5" stroke="currentColor" strokeWidth="1.1" opacity="0.45" />
              <circle cx="200" cy="375" r="118" stroke="currentColor" strokeWidth="1.3" opacity="0.9" />
              <image href="/logo-mark.png" x="128" y="303" width="144" height="144" />
              <text x="200" y="466" textAnchor="middle" className="svg-caps">
                RADROOT
              </text>
              <g transform="rotate(-18 200 375)">
                <ellipse cx="200" cy="375" rx="172" ry="48" stroke="currentColor" strokeWidth="1.2" opacity="0.85" />
              </g>
              <circle cx="130" cy="432" r="16" stroke="currentColor" strokeWidth="1.2" opacity="0.7" />
              <path d="M 186 508 L 214 508 L 200 532 Z" stroke="currentColor" strokeWidth="1.3" />
              <circle cx="200" cy="540" r="2.6" fill="var(--grn-hi)" stroke="none" />
              <g transform="translate(126 568)" opacity="0.85">
                <circle r="9" stroke="currentColor" strokeWidth="1.2" />
                <ellipse rx="16" ry="4.5" stroke="currentColor" strokeWidth="1" opacity="0.7" transform="rotate(-16)" />
              </g>
              <path d={STAR5} transform="translate(272 548) scale(1.25)" stroke="currentColor" strokeWidth="1.1" className="twinkle" />
              <path d={STAR4} transform="translate(74 330) scale(0.9)" fill="var(--grn-hi)" stroke="none" className="twinkle t2" />
              <path d={STAR4} transform="translate(352 396) scale(1.1)" stroke="currentColor" strokeWidth="1.2" className="twinkle t3" />
              <circle cx="112" cy="506" r="2.4" fill="currentColor" opacity="0.5" />
              <circle cx="306" cy="112" r="2" fill="currentColor" opacity="0.4" />
              <circle cx="92" cy="288" r="2" fill="currentColor" opacity="0.35" />
              <circle cx="322" cy="300" r="1.8" fill="currentColor" opacity="0.3" />
              <RingDot reduce={reduce} />
              <InnerDot reduce={reduce} />
              {ELLIPSE_MOVERS.map((m, i) => (
                <EllipseMover key={i} cfg={m} reduce={reduce} />
              ))}
            </svg>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
